import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";

function DeptDialog({ trigger, initial, users, onSaved }) {
  const editing = !!initial;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(initial?.name || "");
  const [managerId, setManagerId] = useState(initial?.manager_id || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { name, manager_id: managerId || null };
      if (editing) await api.put(`/departments/${initial.id}`, body);
      else await api.post("/departments", body);
      toast.success(editing ? "Department updated" : "Department created");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const managers = users.filter((u) => u.role === "manager" || u.role === "super_admin");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">{editing ? "Edit department" : "New department"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-1" data-testid="dept-name" /></div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Manager</Label>
            <Select value={managerId || "none"} onValueChange={(v) => setManagerId(v === "none" ? "" : v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="dept-manager"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Unassigned</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving || !name} className="rounded-none w-full" data-testid="dept-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Departments() {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  const load = () => {
    api.get("/departments").then((r) => setDepartments(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  };
  useEffect(load, []);

  const del = async (id) => { await api.delete(`/departments/${id}`); toast.success("Department deleted"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tighter">Departments</h1>
          <p className="text-sm text-muted-foreground mt-1">Organise fee earners and assign managers.</p>
        </div>
        <DeptDialog users={users} onSaved={load} trigger={<Button className="rounded-none gap-2" data-testid="add-dept-btn"><Plus className="h-4 w-4" /> New department</Button>} />
      </div>

      <div className="border border-border bg-card" data-testid="dept-table">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent"><TableHead>Department</TableHead><TableHead>Manager</TableHead><TableHead className="text-right">Members</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {departments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No departments yet.</TableCell></TableRow>}
            {departments.map((d) => (
              <TableRow key={d.id} className="hover:bg-secondary" data-testid={`dept-row-${d.id}`}>
                <TableCell className="font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-brand" /> {d.name}</TableCell>
                <TableCell className="text-muted-foreground">{d.manager_name || "Unassigned"}</TableCell>
                <TableCell className="text-right font-mono">{d.member_count}</TableCell>
                <TableCell className="flex gap-3 justify-end">
                  <DeptDialog initial={d} users={users} onSaved={load} trigger={<button className="text-muted-foreground hover:text-foreground" data-testid={`edit-dept-${d.id}`}><Pencil className="h-4 w-4" /></button>} />
                  <button onClick={() => del(d.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-dept-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
